'use client'

import * as ToastPrimitives from '@radix-ui/react-toast'
import * as React from 'react'

import { cn, Flex, VStack } from '@podo-app/design-system-temp'

import { useToast } from '../../hooks'
import { ToastVariantProps } from './toaster.type'

import { toastVariants } from './toaster.css'

const ToastProvider = ToastPrimitives.Provider

const ToastViewport = ({ className, ...props }: ToastPrimitives.ToastViewportProps) => (
  <ToastPrimitives.Viewport
    className={cn(
      'fixed left-0 z-[100] flex max-h-screen w-full flex-col-reverse p-2.5 pointer-events-none items-center',
      className,
    )}
    style={{ bottom: `calc(env(safe-area-inset-bottom, 0px) + 8px)` }}
    {...props}
  />
)

const Toast = ({ className, variant, ...props }: ToastPrimitives.ToastProps & ToastVariantProps) => {
  return <ToastPrimitives.Root className={cn(toastVariants({ variant }), className)} {...props} />
}

const ToastAction = ({ className, ...props }: ToastPrimitives.ToastActionProps) => (
  <ToastPrimitives.Action
    className={cn(
      'inline-flex h-5 shrink-0 items-center justify-center rounded-[3.75px] border bg-transparent px-[7.5px] text-sm font-medium transition-colors hover:bg-secondary focus:outline-none focus:ring-1 focus:ring-ring disabled:pointer-events-none disabled:opacity-50 group-[.destructive]:border-muted/40 group-[.destructive]:hover:border-destructive/30 group-[.destructive]:hover:bg-destructive group-[.destructive]:hover:text-destructive-foreground group-[.destructive]:focus:ring-destructive',
      className,
    )}
    {...props}
  />
)

const ToastClose = ({ className, ...props }: ToastPrimitives.ToastCloseProps) => (
  <ToastPrimitives.Close
    className={cn(
      'absolute right-[2.5px] top-[2.5px] rounded-[3.75px] p-[2.5px] text-foreground/50 opacity-0 transition-opacity hover:text-foreground focus:opacity-100 focus:outline-none focus:ring-1 group-hover:opacity-100 group-[.destructive]:text-red-300 group-[.destructive]:hover:text-red-50 group-[.destructive]:focus:ring-red-400 group-[.destructive]:focus:ring-offset-red-600',
      className,
    )}
    toast-close=""
    {...props}
  />
)

const ToastTitle = ({ className, ...props }: ToastPrimitives.ToastTitleProps) => (
  <Flex
    as={ToastPrimitives.Title}
    className={cn('text-sm font-medium justify-center [&+div]:text-xs', className)}
    {...props}
  />
)

const ToastDescription = ({ className, ...props }: ToastPrimitives.ToastDescriptionProps) => (
  <ToastPrimitives.Description className={cn('text-sm opacity-90 text-center', className)} {...props} />
)

const Toaster = () => {
  const { toasts, dismiss } = useToast()

  return (
    <ToastProvider swipeDirection="down" duration={3000}>
      {toasts.map(({ id, title, description, action, position, ...props }) => (
        <Toast
          key={id}
          {...props}
          className="bg-gray-900 ToastRoot pointer-events-auto cursor-pointer"
          style={{ ...position }}
          onClick={() => dismiss(id)}
        >
          <VStack className="flex-1">
            {title && <ToastTitle>{title}</ToastTitle>}
            {description && <ToastDescription>{description}</ToastDescription>}
          </VStack>
          {action}
        </Toast>
      ))}
      <ToastViewport />
    </ToastProvider>
  )
}

export { Toast, ToastAction, ToastClose, ToastDescription, Toaster, ToastProvider, ToastTitle, ToastViewport }
